import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { ICouncilSession } from "dto/Council/ICouncil";
import {
  ICouncilProtocolDocumentRef,
  ICouncilProtocolItem,
  ISaveCouncilProtocolItemRequest,
  ICouncilCouncilor,
  ICouncilPortalSettings,
  ICouncilSessionConduct,
  ICouncilVoting,
  ISaveAnalogCouncilVotingRequest,
  ISaveCouncilCouncilorRequest,
  ISaveCouncilPortalSettingsRequest,
  ISaveCouncilProtocolRequest,
  IStartCouncilVotingRequest,
} from "dto/Council/ICouncilPortal";

// Biuro rady, ETAP 2 — administracja portalem radnego i prowadzenie obrad.
// To API aplikacji GŁÓWNEJ (pracownik urzędu, token OIDC). Sam portal radnego
// jest osobną aplikacją i rozmawia z api/councilportal/* na własnym tokenie.

export const councilPortalQueryKeys = {
  settings: "council.portal.settings",
  councilors: "council.councilors",
  conduct: "council.conduct",
  votings: "council.votings",
  protocolItems: "council.protocol.items",
  protocolDocument: "council.protocol.document",
};

// ── Ustawienia portalu ─────────────────────────────────────────────────────

const getPortalSettings = (): Promise<{ data: ICouncilPortalSettings }> =>
  axiosInstance.get("council/portal/settings");

const useGetPortalSettings = () =>
  useCustomQuery(councilPortalQueryKeys.settings, () => getPortalSettings());

const savePortalSettings = (
  payload: ISaveCouncilPortalSettingsRequest,
): Promise<{ data: ICouncilPortalSettings }> =>
  axiosInstance.post("council/portal/settings", payload);

const useSavePortalSettings = () => useCustomMutation(savePortalSettings);

// ── Radni ──────────────────────────────────────────────────────────────────

const getCouncilors = (): Promise<{ data: ICouncilCouncilor[] }> =>
  axiosInstance.get("council/councilors");

const useGetCouncilors = () =>
  useCustomQuery(councilPortalQueryKeys.councilors, () => getCouncilors());

const saveCouncilor = (
  payload: ISaveCouncilCouncilorRequest,
): Promise<{ data: ICouncilCouncilor }> =>
  axiosInstance.post("council/councilors", payload);

const useSaveCouncilor = () => useCustomMutation(saveCouncilor);

const setCouncilorPassword = (payload: {
  id: string;
  password: string;
}): Promise<{ data: unknown }> =>
  axiosInstance.post(`council/councilors/${payload.id}/password`, {
    Password: payload.password,
  });

const useSetCouncilorPassword = () => useCustomMutation(setCouncilorPassword);

const removeCouncilor = (id: string): Promise<{ data: unknown }> =>
  axiosInstance.delete(`council/councilors/${id}`);

const useRemoveCouncilor = () => useCustomMutation(removeCouncilor);

// ── Prowadzenie obrad ──────────────────────────────────────────────────────

const getConduct = (
  sessionId: string,
): Promise<{ data: ICouncilSessionConduct }> =>
  axiosInstance.get(`council/sessions/${sessionId}/conduct`);

/**
 * Panel prowadzącego odświeża się cyklicznie — radni głosują i meldują obecność
 * z portalu, więc bez odpytywania panel pokazywałby stan sprzed ich akcji.
 */
const useGetConduct = (sessionId?: string, refetchInterval?: number) =>
  useCustomQuery(
    [councilPortalQueryKeys.conduct, sessionId],
    () => getConduct(sessionId as string),
    { enabled: !!sessionId, refetchInterval },
  );

const sessionAction = (payload: {
  sessionId: string;
  action: "start" | "suspend" | "resume" | "end" | "close";
}): Promise<{ data: ICouncilSession }> =>
  axiosInstance.post(`council/sessions/${payload.sessionId}/${payload.action}`);

const useSessionAction = () => useCustomMutation(sessionAction);

const setActiveAgendaItem = (payload: {
  sessionId: string;
  itemId: string;
}): Promise<{ data: unknown }> =>
  axiosInstance.post(
    `council/sessions/${payload.sessionId}/active-item/${payload.itemId}`,
  );

const useSetActiveAgendaItem = () => useCustomMutation(setActiveAgendaItem);

const setPresence = (payload: {
  sessionId: string;
  councilorId: string;
  isPresent: boolean;
}): Promise<{ data: unknown }> =>
  axiosInstance.post(`council/sessions/${payload.sessionId}/presence`, {
    CouncilorId: payload.councilorId,
    IsPresent: payload.isPresent,
  });

const useSetPresence = () => useCustomMutation(setPresence);

const letSpeak = (speakQueueItemId: string): Promise<{ data: unknown }> =>
  axiosInstance.post(`council/speak-queue/${speakQueueItemId}/let-speak`);

const useLetSpeak = () => useCustomMutation(letSpeak);

// ── Głosowania ─────────────────────────────────────────────────────────────

const getVotings = (sessionId: string): Promise<{ data: ICouncilVoting[] }> =>
  axiosInstance.get(`council/sessions/${sessionId}/votings`);

const useGetVotings = (sessionId?: string, refetchInterval?: number) =>
  useCustomQuery(
    [councilPortalQueryKeys.votings, sessionId],
    () => getVotings(sessionId as string),
    { enabled: !!sessionId, refetchInterval },
  );

const startVoting = (payload: {
  sessionId: string;
  request: IStartCouncilVotingRequest;
}): Promise<{ data: ICouncilVoting }> =>
  axiosInstance.post(
    `council/sessions/${payload.sessionId}/votings/start`,
    payload.request,
  );

const useStartVoting = () => useCustomMutation(startVoting);

const endVoting = (votingId: string): Promise<{ data: ICouncilVoting }> =>
  axiosInstance.post(`council/votings/${votingId}/end`);

const useEndVoting = () => useCustomMutation(endVoting);

const cancelVoting = (votingId: string): Promise<{ data: ICouncilVoting }> =>
  axiosInstance.post(`council/votings/${votingId}/cancel`);

const useCancelVoting = () => useCustomMutation(cancelVoting);

const removeVoting = (votingId: string): Promise<{ data: unknown }> =>
  axiosInstance.delete(`council/votings/${votingId}`);

const useRemoveVoting = () => useCustomMutation(removeVoting);

const saveAnalogVoting = (payload: {
  sessionId: string;
  request: ISaveAnalogCouncilVotingRequest;
}): Promise<{ data: ICouncilVoting }> =>
  axiosInstance.post(
    `council/sessions/${payload.sessionId}/votings/analog`,
    payload.request,
  );

const useSaveAnalogVoting = () => useCustomMutation(saveAnalogVoting);

// ── Dziennik przebiegu sesji ───────────────────────────────────────────────

const getProtocolItems = (
  sessionId: string,
): Promise<{ data: ICouncilProtocolItem[] }> =>
  axiosInstance.get(`council/sessions/${sessionId}/protocol-items`);

const useGetProtocolItems = (sessionId?: string, refetchInterval?: number) =>
  useCustomQuery(
    [councilPortalQueryKeys.protocolItems, sessionId],
    () => getProtocolItems(sessionId as string),
    { enabled: !!sessionId, refetchInterval },
  );

const saveProtocolItem = (payload: {
  sessionId: string;
  request: ISaveCouncilProtocolItemRequest;
}): Promise<{ data: ICouncilProtocolItem }> =>
  axiosInstance.post(
    `council/sessions/${payload.sessionId}/protocol-items`,
    payload.request,
  );

const useSaveProtocolItem = () => useCustomMutation(saveProtocolItem);

const removeProtocolItem = (itemId: string): Promise<{ data: unknown }> =>
  axiosInstance.delete(`council/protocol-items/${itemId}`);

const useRemoveProtocolItem = () => useCustomMutation(removeProtocolItem);

// ── Dokument protokołu ─────────────────────────────────────────────────────

const getProtocolDocument = (
  sessionId: string,
): Promise<{ data: ICouncilProtocolDocumentRef }> =>
  axiosInstance.get(`council/sessions/${sessionId}/protocol-document`);

const useGetProtocolDocument = (sessionId?: string) =>
  useCustomQuery(
    [councilPortalQueryKeys.protocolDocument, sessionId],
    () => getProtocolDocument(sessionId as string),
    { enabled: !!sessionId },
  );

/** Wiąże złożony dokument z sesją. Jednorazowo — protokół powstaje raz. */
const bindProtocolDocument = (payload: {
  sessionId: string;
  documentId: string;
}): Promise<{ data: ICouncilProtocolDocumentRef }> =>
  axiosInstance.post(`council/sessions/${payload.sessionId}/protocol-document`, {
    DocumentId: payload.documentId,
  });

const useBindProtocolDocument = () => useCustomMutation(bindProtocolDocument);

// ── Protokół (wgrany plik PDF) ─────────────────────────────────────────────

const saveProtocol = (payload: {
  sessionId: string;
  request: ISaveCouncilProtocolRequest;
}): Promise<{ data: ICouncilSession }> =>
  axiosInstance.post(`council/sessions/${payload.sessionId}/protocol`, payload.request);

const useSaveProtocol = () => useCustomMutation(saveProtocol);

export default {
  useGetPortalSettings,
  useSavePortalSettings,
  useGetCouncilors,
  useSaveCouncilor,
  useSetCouncilorPassword,
  useRemoveCouncilor,
  useGetConduct,
  useSessionAction,
  useSetActiveAgendaItem,
  useSetPresence,
  useLetSpeak,
  useGetVotings,
  useStartVoting,
  useEndVoting,
  useCancelVoting,
  useRemoveVoting,
  useSaveAnalogVoting,
  useSaveProtocol,
  useGetProtocolItems,
  useSaveProtocolItem,
  useRemoveProtocolItem,
  useGetProtocolDocument,
  useBindProtocolDocument,
};
